// src/pages/courtowner/ManageSlots.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { X, ChevronLeft, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import CourtOwnerSidebar from "../../components/courtowner/CourtOwnerSidebar";

const API_BASE = "https://api.getfitindia.in/api";

/* ========= Utilities ========= */
const pad2 = (n) => String(n).padStart(2, "0");
const priceToNumber = (p) =>
  p === "" || p == null ? 0 : Number(String(p).replace(/[^\d]/g, ""));
const toMinutes = (t) => {
  if (!t) return NaN;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
};
const fromMinutes = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${pad2(h)}:${pad2(m)}`;
};
const formatAMPM = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${ampm}`;
};
const buildHalfHourOptions = (start = 5 * 60, end = 23 * 60 + 30) => {
  const out = [];
  for (let m = start; m <= end; m += 30) out.push(fromMinutes(m));
  return out;
};

// overlaps / dedupe
const hasOverlap = (list) => {
  const ranges = list
    .map(({ startTime, endTime }) => ({
      s: toMinutes(startTime),
      e: toMinutes(endTime),
    }))
    .filter(({ s, e }) => Number.isFinite(s) && Number.isFinite(e) && e > s)
    .sort((a, b) => a.s - b.s);
  for (let i = 1; i < ranges.length; i++) if (ranges[i].s < ranges[i - 1].e) return true;
  return false;
};
const dedupeByStartEnd = (arr) => {
  const seen = new Set();
  return arr.filter(({ startTime, endTime }) => {
    const k = `${startTime}|${endTime}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};
const overlapsExisting = (slot, existing) => {
  const s = toMinutes(slot.startTime);
  const e = toMinutes(slot.endTime);
  if (!(e > s)) return true;
  for (const ex of existing) {
    const es = toMinutes(ex.startTime);
    const ee = toMinutes(ex.endTime);
    if (!Number.isFinite(es) || !Number.isFinite(ee)) continue;
    if (es < e && s < ee) return true;
  }
  return false;
};

// period helpers
const getPeriodWindow = (period) => {
  if (period === "afternoon") return { startMin: 12 * 60 + 30, endMin: 16 * 60 + 30 };
  if (period === "evening") return { startMin: 16 * 60 + 30, endMin: 23 * 60 + 59 };
  return { startMin: 5 * 60, endMin: 12 * 60 + 30 };
};
const buildPeriodSlots = (period) => {
  const { startMin, endMin } = getPeriodWindow(period);
  const out = [];
  let m = startMin;
  while (m < endMin) {
    const s = fromMinutes(m);
    m += 30;
    const e = fromMinutes(m);
    out.push({ startTime: s, endTime: e });
  }
  return out;
};

/* ========= Court helpers (normalize + cache) ========= */
const getCourtId = (c) => (c?._id ?? c?.id ?? "");
const getCourtName = (c) => (c?.courtName ?? c?.name ?? "");
const uniqueCourtsById = (arr) => {
  const map = new Map();
  for (const c of Array.isArray(arr) ? arr : []) map.set(getCourtId(c), c);
  return Array.from(map.values());
};
const normalizeCourtsResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.courts)) return data.courts;
  return [];
};

/* ====== Helper to compute non-overlapping end times for a given start ====== */
const computeEndOptions = (startTime, occupiedSlots, allTimes) => {
  if (!startTime) return [];
  const startMin = toMinutes(startTime);

  return allTimes
    .filter((t) => toMinutes(t) > startMin) // end must be after start
    .filter((end) => {
      const candidate = { startTime, endTime: end };
      return !overlapsExisting(candidate, occupiedSlots);
    });
};

/* ========= Component ========= */
export default function ManageSlots() {
  const navigate = useNavigate();

  // data
  const [venues, setVenues] = useState([]);
  const [courts, setCourts] = useState([]);

  // selections
  const [venueId, setVenueId] = useState("");
  const [courtId, setCourtId] = useState("");

  // name caching (prevents losing label after save/refresh)
  const [courtNameCache, setCourtNameCache] = useState({}); // { [id]: name }

  // NEW: add court UI state
  const [showAddCourt, setShowAddCourt] = useState(false);
  const [newCourtName, setNewCourtName] = useState("");
  const [newCourtSport, setNewCourtSport] = useState(""); // optional
  const [creatingCourt, setCreatingCourt] = useState(false);

  // inputs
  const [date, setDate] = useState(""); // Start Date (now used!)
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [price, setPrice] = useState("");
  const [defaultPrice, setDefaultPrice] = useState("");
  const [updatePeriodChoice, setUpdatePeriodChoice] = useState("morning");
  const [saveMode, setSaveMode] = useState("merge"); // merge | replace

  // slots
  const [existingSlots, setExistingSlots] = useState([]);
  const [slotList, setSlotList] = useState([]);

  const [saving, setSaving] = useState(false);

  const timeOptions = useMemo(buildHalfHourOptions, []);
  const totalAmount = useMemo(
    () => slotList.reduce((sum, s) => sum + priceToNumber(s.price), 0),
    [slotList]
  );

  const getAuthHeader = useCallback(() => {
    // More resilient: support either key
    const token = localStorage.getItem("courtOwnerToken"); // ONLY use this!
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  /* ===== Load venues ===== */
  useEffect(() => {
  (async () => {
    try {
      const res = await fetch(`${API_BASE}/courtowner/venues`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
      });

      if (res.status === 403) {
        alert("Access denied: Please login again as Court Owner.");
        localStorage.removeItem("courtOwnerToken");
        navigate("/courtowner/login");
        return;
      }
      
      if (!res.ok) throw new Error(`Failed to load venues [HTTP ${res.status}]`);

      const data = await res.json();
      setVenues(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("🚨 Venue loading error:", err);
      alert(err.message || "Error loading venues");
    }
  })();
}, [getAuthHeader]);

  /* ===== Load courts when venue changes ===== */
  useEffect(() => {
    setCourts([]);
    setCourtId("");
    setExistingSlots([]);
    setSlotList([]);
    if (!venueId) return;

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/slots/${venueId}/courts`, {
          headers: { ...getAuthHeader() },
        });
        if (!res.ok) throw new Error(`Failed to load courts [HTTP ${res.status}]`);
        const data = await res.json();
        const list = uniqueCourtsById(normalizeCourtsResponse(data));
        setCourts(list);

        // update cache with any names we have
        setCourtNameCache((prev) => {
          const next = { ...prev };
          for (const c of list) {
            const id = getCourtId(c);
            const nm = getCourtName(c);
            if (id && nm) next[id] = nm;
          }
          return next;
        });
      } catch (err) {
        console.error(err);
        alert("Failed to load courts");
      }
    })();
  }, [venueId, getAuthHeader]);

  /* ===== When court changes, show its existing slots ===== */
  useEffect(() => {
    const court = courts.find((c) => getCourtId(c) === courtId);
    setExistingSlots(Array.isArray(court?.slots) ? court.slots : []);

    // keep cache updated if API provided a name now
    if (court) {
      const id = getCourtId(court);
      const nm = getCourtName(court);
      if (id && nm) {
        setCourtNameCache((prev) => (prev[id] ? prev : { ...prev, [id]: nm }));
      }
    }
    setSlotList([]);
  }, [courtId, courts]);

  /* ===== Refresh courts after save or add ===== */
  const refreshCourts = async (selectCourtId) => {
    if (!venueId) return;
    const res = await fetch(`${API_BASE}/slots/${venueId}/courts`, {
      headers: { ...getAuthHeader() },
    });
    if (!res.ok) throw new Error("Failed to refresh courts");
    const data = await res.json();
    const list = uniqueCourtsById(normalizeCourtsResponse(data));
    setCourts(list);

    // update cache with any names present in refreshed list
    setCourtNameCache((prev) => {
      const next = { ...prev };
      for (const c of list) {
        const id = getCourtId(c);
        const nm = getCourtName(c);
        if (id && nm) next[id] = nm;
      }
      return next;
    });

    // re-pick the selected court's slots and keep its label via cache
    const selectedId = selectCourtId || courtId;
    if (selectedId) {
      setCourtId(selectedId);
      const court = list.find((c) => getCourtId(c) === selectedId);
      setExistingSlots(Array.isArray(court?.slots) ? court.slots : []);
    }
  };

  /* ====== Build "occupied slots" for the selected date ======
     We only care about slots that apply for this date
  */
  const slotsForDateExisting = useMemo(
    () =>
      date
        ? existingSlots.filter((s) => !s.startDate || s.startDate === date)
        : existingSlots,
    [existingSlots, date]
  );

  const slotsForDateWorking = useMemo(
    () => (date ? slotList.filter((s) => s.startDate === date) : slotList),
    [slotList, date]
  );

  const allOccupiedForDate = useMemo(
    () => [...slotsForDateExisting, ...slotsForDateWorking],
    [slotsForDateExisting, slotsForDateWorking]
  );

  /* ===== Available start & end times (only free slots) ===== */
  const availableStartTimes = useMemo(() => {
    if (!date) return timeOptions; // no date selected => show all
    return timeOptions.filter((t) => {
      const endOptions = computeEndOptions(t, allOccupiedForDate, timeOptions);
      return endOptions.length > 0;
    });
  }, [date, timeOptions, allOccupiedForDate]);

  const endTimeOptions = useMemo(
    () => computeEndOptions(startTime, allOccupiedForDate, timeOptions),
    [startTime, allOccupiedForDate, timeOptions]
  );

  // If startTime changes and current endTime is no longer valid, clear it
  useEffect(() => {
    if (!startTime) {
      setEndTime("");
      return;
    }
    const validEnds = computeEndOptions(startTime, allOccupiedForDate, timeOptions);
    if (!validEnds.includes(endTime)) {
      setEndTime("");
    }
  }, [startTime, allOccupiedForDate, timeOptions, endTime]);

  // const canAddSlot =
  //   !!date && // require Start Date
  //   !!startTime &&
  //   !!endTime &&
  //   Number.isFinite(toMinutes(startTime)) &&
  //   Number.isFinite(toMinutes(endTime)) &&
  //   toMinutes(endTime) > toMinutes(startTime) &&
  //   priceToNumber(price || defaultPrice) > 0;
const effectivePrice =
  price !== "" ? priceToNumber(price) : priceToNumber(defaultPrice);

const canAddSlot =
  !!date &&
  !!startTime &&
  !!endTime &&
  Number.isFinite(toMinutes(startTime)) &&
  Number.isFinite(toMinutes(endTime)) &&
  toMinutes(endTime) > toMinutes(startTime) &&
  effectivePrice > 0;

  /* ===== Manual Add / Remove ===== */
 const addSlot = () => {
  if (!canAddSlot) {
    if (!date) alert("Please select Start Date before adding slot.");
    return;
  }

  // const priceValue = priceToNumber(price || defaultPrice);
  const priceValue = price !== "" ? priceToNumber(price) : priceToNumber(defaultPrice);

  const startMin = toMinutes(startTime);
  const endMin = toMinutes(endTime);

  // 1️⃣ First, make sure the whole range doesn't overlap anything
  const rangeSlot = {
    startTime,
    endTime,
    price: priceValue,
    startDate: date,
  };

  if (overlapsExisting(rangeSlot, allOccupiedForDate)) {
    alert("This time range overlaps with an existing slot.");
    return;
  }

  // 2️⃣ Split into 30-minute chunks
  const newSlots = [];
  for (let m = startMin; m < endMin; m += 30) {
    const s = fromMinutes(m);
    const e = fromMinutes(m + 30);

    // safety: only add valid chunks
    if (toMinutes(e) <= toMinutes(s)) continue;

    newSlots.push({
      startTime: s,
      endTime: e,
      price: priceValue,   // price per 30-minute slot
      startDate: date,
    });
  }

  // 3️⃣ Merge with current working list, sort by time
  const next = dedupeByStartEnd([...slotList, ...newSlots]).sort(
    (a, b) => toMinutes(a.startTime) - toMinutes(b.startTime)
  );

  setSlotList(next);

  // 4️⃣ Clear inputs
  setStartTime("");
  setEndTime("");
  setPrice("");
};

  const removeSlot = (idx) =>
    setSlotList((prev) => prev.filter((_, i) => i !== idx));

  const clearAll = () => setSlotList([]);

  /* ===== Load Existing -> Working ===== */
  const loadExistingToWorking = () => {
    if (!existingSlots.length) {
      alert("No existing slots found for this court.");
      return;
    }
    const next = existingSlots
      .map((s) => ({
        startTime: s.startTime,
        endTime: s.endTime,
        price: priceToNumber(s.price),
        startDate: s.startDate || "",
      }))
      .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
    setSlotList(next);
  };

  /* ===== Add Period (append) ===== */
  const addPeriod = (period) => {
    if (!date) {
      alert("Please select Start Date before generating slots.");
      return;
    }

    const dPrice = priceToNumber(defaultPrice);
    if (!dPrice) {
      alert("Set a Default Price first to generate slots.");
      return;
    }

    const gen = buildPeriodSlots(period);
    const existing = [...allOccupiedForDate];
    const toAdd = [];
    for (const g of gen) {
      const candidate = { ...g, price: dPrice, startDate: date };
      if (overlapsExisting(candidate, existing)) continue;
      toAdd.push(candidate);
      existing.push(candidate);
    }
    if (!toAdd.length) {
      alert("No new slots were added (duplicates or overlaps).");
      return;
    }
    const next = dedupeByStartEnd([...slotList, ...toAdd]).sort(
      (a, b) => toMinutes(a.startTime) - toMinutes(b.startTime)
    );
    setSlotList(next);
  };

  /* ===== Single Update Button (replace only chosen period within working list) ===== */
  const updateChosenPeriod = () => {
    if (!date) {
      alert("Please select Start Date before updating a period.");
      return;
    }

    const period = updatePeriodChoice;
    const dPrice = priceToNumber(defaultPrice);
    if (!dPrice) {
      alert("Set a Default Price first to update a period.");
      return;
    }

    const { startMin, endMin } = getPeriodWindow(period);
    const outside = slotList.filter((s) => {
      const ss = toMinutes(s.startTime);
      const ee = toMinutes(s.endTime);
      return !(ss >= startMin && ee <= endMin);
    });

    const generated = buildPeriodSlots(period);
    const base = [...outside, ...slotsForDateExisting]; // also consider existing
    const toAdd = [];
    for (const g of generated) {
      const candidate = { ...g, price: dPrice, startDate: date };
      if (overlapsExisting(candidate, base)) continue;
      toAdd.push(candidate);
      base.push(candidate);
    }

    const next = dedupeByStartEnd([...outside, ...toAdd]).sort(
      (a, b) => toMinutes(a.startTime) - toMinutes(b.startTime)
    );
    setSlotList(next);
  };

  /* ===== Helpers ===== */
  const selectedCourtObj = courts.find((c) => getCourtId(c) === courtId) || null;
  const selectedCourtName =
    getCourtName(selectedCourtObj) || courtNameCache[courtId] || "Selected Court";
  const selectedCourtSports = Array.isArray(selectedCourtObj?.sports)
    ? selectedCourtObj.sports
    : [];

  /* ===== NEW: Create Court handler ===== */
  const handleCreateCourt = async (e) => {
    e.preventDefault();
    if (!venueId) {
      alert("Please select a property (venue) first.");
      return;
    }
    if (!newCourtName.trim()) {
      alert("Please enter court name.");
      return;
    }

    try {
      setCreatingCourt(true);
      const payload = {
        courtName: newCourtName.trim(),
        sports: newCourtSport ? [newCourtSport.trim()] : [],
        slots: [],
      };

      const res = await fetch(`${API_BASE}/slots/${venueId}/courts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error("Create court failed", { status: res.status, data });
        throw new Error(
          data?.error || data?.message || `Failed to create court (HTTP ${res.status})`
        );
      }

      const createdId = getCourtId(data);
      const createdName = getCourtName(data) || newCourtName.trim();

      if (createdId && createdName) {
        setCourtNameCache((prev) => ({ ...prev, [createdId]: createdName }));
      }

      alert("Court created successfully!");
      setShowAddCourt(false);
      setNewCourtName("");
      setNewCourtSport("");

      await refreshCourts(createdId || undefined);
    } catch (err) {
      console.error(err);
      alert(err.message || "Network error while creating court");
    } finally {
      setCreatingCourt(false);
    }
  };

  /* ===== Save -> PUT (MERGE by default) ===== */
  const saveAll = async () => {
    if (!venueId) return alert("Please select a property (venue).");
    if (!courtId) return alert("Please select a court.");
    if (!slotList.length) return alert("Please add at least one slot.");

    const working = slotList.map((s) => ({
      startTime: s.startTime,
      endTime: s.endTime,
      price: priceToNumber(s.price),
      startDate: s.startDate || date || undefined,
    }));

    let finalToSave = working;

    if (saveMode === "merge") {
      const existingNormalized = (existingSlots || []).map((s) => ({
        startTime: s.startTime,
        endTime: s.endTime,
        price: priceToNumber(s.price),
        startDate: s.startDate,
      }));
      finalToSave = dedupeByStartEnd([...existingNormalized, ...working]).sort(
        (a, b) => toMinutes(a.startTime) - toMinutes(b.startTime)
      );
    }

    if (hasOverlap(finalToSave)) {
      alert(
        saveMode === "merge"
          ? "Merged slot list has overlaps. Please resolve conflicts."
          : "Slots overlap. Please fix before saving."
      );
      return;
    }

    const payload = {
      courtName: selectedCourtName || "Court",
      sports: selectedCourtSports,
      slots: finalToSave,
    };

    try {
      setSaving(true);
      const url = `${API_BASE}/slots/${venueId}/courts/${courtId}`;
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        console.error("Save failed", { status: res.status, data });
        throw new Error(data?.error || `Failed to save slots (HTTP ${res.status})`);
      }

      alert(saveMode === "merge" ? "Slots merged & saved!" : "Slots replaced & saved!");
      setSlotList([]);
      await refreshCourts();
    } catch (err) {
      console.error(err);
      alert(err.message || "Network error");
    } finally {
      setSaving(false);
    }
  };

  /* ===== Layout ===== */
  return (
    <div className="min-h-screen bg-gray-50">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-white shadow z-30">
        <CourtOwnerSidebar />
      </aside>

      <main className="lg:pl-64">
        <div className="bg-[#0f2250] text-white sticky top-0 z-20">
          <div className="max-w-6xl  px-4 py-4 flex items-center gap-2">
            {/* <button
              onClick={() => navigate(-1)}
              className="p-1 rounded hover:bg-white/10"
              aria-label="Back"
            >
              <ChevronLeft />
            </button> */}
            <div className="lg:hidden mt-10">
                          <CourtOwnerSidebar />
                        </div>
            <h1 className="text-lg font-semibold  sm:ms-0 md:ms-16 lg:ms-0 mt-1 ">Slot Details</h1>
          </div>
        </div>

        <div className="max-w-6xl mx-0  px-4 ">
          <div className=" md:p-6 mt-6 relative z-0">
            {/* Venue & Court */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className="text-gray-700 text-sm font-medium mb-1">
                  Select Property
                </label>
                <select
                  className="h-9 rounded-lg text-sm border border-gray-300 px-3"
                  value={venueId}
                  onChange={(e) => setVenueId(e.target.value)}
                >
                  <option value="">Select Property</option>
                  {venues.map((v) => (
                    <option key={v._id} value={v._id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="text-gray-700 text-sm font-medium mb-1">
                  Select Court
                </label>
                <div className="flex gap-2">
                  <select
                    className="h-9 rounded-lg text-sm border border-gray-300 px-3 flex-1"
                    value={courtId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setCourtId(id);
                      const c = courts.find((x) => getCourtId(x) === id);
                      const nm = getCourtName(c);
                      if (id && nm) {
                        setCourtNameCache((prev) => ({ ...prev, [id]: nm }));
                      }
                    }}
                    disabled={!venueId || courts.length === 0}
                  >
                    <option value="">Select Court</option>
                    {courts.map((c, idx) => {
                      const id = getCourtId(c);
                      const nm = getCourtName(c) || courtNameCache[id] || `court ${idx + 1}`;
                      return (
                        <option key={id || idx} value={id}>
                          {nm}
                        </option>
                      );
                    })}
                  </select>

                  <button
                    type="button"
                    onClick={() => setShowAddCourt((s) => !s)}
                    disabled={!venueId}
                    className={`h-9 px-4 rounded-lg text-sm font-semibold ${
                      venueId
                        ? "bg-[#0f2250] text-white hover:opacity-90"
                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    + Add Court
                  </button>
                </div>

                {/* Add Court inline panel */}
                {showAddCourt && (
                  <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-700">
                        Create New Court
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowAddCourt(false)}
                        className="p-1 rounded-full hover:bg-gray-200"
                        aria-label="Close add court"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <form onSubmit={handleCreateCourt} className="space-y-2">
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">
                          Court Name
                        </label>
                        <input
                          type="text"
                          className="w-full h-9 rounded-md border border-gray-300 px-2 text-sm"
                          placeholder="e.g. Court 1, Badminton Court A"
                          value={newCourtName}
                          onChange={(e) => setNewCourtName(e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="text-xs text-gray-600 block mb-1">
                          Sport (optional)
                        </label>
                        <input
                          type="text"
                          className="w-full h-9 rounded-md border border-gray-300 px-2 text-sm"
                          placeholder="e.g. Badminton, Football"
                          value={newCourtSport}
                          onChange={(e) => setNewCourtSport(e.target.value)}
                        />
                      </div>

                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            setShowAddCourt(false);
                            setNewCourtName("");
                            setNewCourtSport("");
                          }}
                          className="h-9 px-3 rounded-md border border-gray-300 text-xs"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={creatingCourt || !newCourtName.trim()}
                          className={`h-9 px-4 rounded-md text-xs font-semibold ${
                            creatingCourt || !newCourtName.trim()
                              ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                              : "bg-[#0f2250] text-white hover:opacity-90"
                          }`}
                        >
                          {creatingCourt ? "Creating..." : "Create Court"}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>

            {/* Start Date */}
            <div className="mt-4">
              <label className="text-gray-700 text-sm font-medium mb-1 block">
                Start Date
              </label>
              <input
                type="date"
                className="h-9 w-full text-sm  rounded-lg border border-gray-300 px-3"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            {/* Time pickers */}
            <div className="grid grid-cols-2 gap-4 mt-4">
  <div className="flex flex-col">
    <label className="text-gray-700 text-sm font-medium mb-1">
      Start Time
    </label>
    <select
      className="h-9 rounded-lg text-sm border border-gray-300 px-3"
      value={startTime}
      onChange={(e) => setStartTime(e.target.value)}
    >
      <option value="">Start Time</option>
      {availableStartTimes.map((t) => (
        <option key={t} value={t}>
          {formatAMPM(t)}
        </option>
      ))}
    </select>
  </div>

  <div className="flex flex-col">
    <label className="text-gray-700 text-sm font-medium mb-1">
      End Time
    </label>
    <select
      className="h-9 rounded-lg text-sm border border-gray-300 px-3"
      value={endTime}
      onChange={(e) => setEndTime(e.target.value)}
      disabled={!startTime}
    >
      <option value="">End Time</option>
      {endTimeOptions.map((t) => (
        <option key={t} value={t}>
          {formatAMPM(t)}
        </option>
      ))}
    </select>
  </div>
</div>


            {/* Default Price */}
             <div className="grid grid-cols-2 gap-4 mt-4">
           <div className="mt-4">
  <label className="text-gray-700 text-sm font-medium mb-1 block">
    <span
      title="Applies to generated slots"
      className="inline-block "
    >
      Default Price
    </span>
  </label>

  <div className="flex items-center h-9 rounded-lg border border-gray-300 px-3 ">
    <span className="text-gray-600 text-sm  mr-2">₹</span>
    <input
      type="text"
      inputMode="numeric"
      className="w-full outline-none"
      placeholder="0"
      value={defaultPrice}
      onChange={(e) =>
        setDefaultPrice(e.target.value.replace(/[^\d]/g, ""))
      }
    />
  </div>
</div>

 <div className="mt-4">
             <label className="text-gray-700 text-sm font-medium mb-1 block">
                  Price for this slot
                </label>
                <div className="flex items-center h-9 rounded-lg border border-gray-300 px-3">
                  <span className="text-gray-600 text-sm mr-2">₹</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full outline-none"
                    placeholder={defaultPrice ? `e.g. ${defaultPrice}` : "0"}
                    value={price}
                    onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ""))}
                  />
                </div>
     </div></div>
            {/* Per-slot Price + Add */}
            <div className="mt-4 grid md:grid-cols-[1fr_auto] gap-4 items-end">
              <div>
                {/* <label className="text-gray-700 font-medium mb-1 block">
                  Price for this slot
                </label>
                <div className="flex items-center h-9 rounded-lg border border-gray-300 px-3">
                  <span className="text-gray-600 mr-2">₹</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    className="w-full outline-none"
                    placeholder={defaultPrice ? `e.g. ${defaultPrice}` : "0"}
                    value={price}
                    onChange={(e) => setPrice(e.target.value.replace(/[^\d]/g, ""))}
                  />
                </div> */}

                <div className="mt-3">
                  <label className="text-gray-700 text-sm font-medium mb-1 block">
                    Total (all slots in working list)
                  </label>
                  <div className="flex items-center h-9 rounded-lg border border-gray-300 px-3 bg-gray-50">
                    <span className="text-gray-600 text-sm mr-2">₹</span>
                    <input
                      type="text"
                      className="w-full outline-none bg-transparent"
                      value={String(totalAmount)}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={addSlot}
                disabled={!canAddSlot}
                className={`h-9 px-6 rounded-lg font-semibold ${
                  canAddSlot
                    ? "bg-[#0f2250] text-white hover:opacity-90"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`}
              >
                ADD SLOT
              </button>
            </div>

            {/* Period Quick-Add / Single Update / Save mode */}
            <div className="mt-4 grid gap-4 md:grid-cols-[1fr_auto]">
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => addPeriod("morning")}
                  className="bg-blue-300 text-white text-sm px-3 h-9 rounded-lg font-medium hover:bg-blue-300 hover:text-black"
                >
                  Morning
                </button>
                <button
                  type="button"
                  onClick={() => addPeriod("afternoon")}
                  className="bg-blue-300 text-white text-sm px-3 h-9 rounded-lg font-medium hover:bg-blue-300 hover:text-black"
                >
                  Afternoon
                </button>
                <button
                  type="button"
                  onClick={() => addPeriod("evening")}
                  className="bg-blue-300 text-white text-sm px-3 h-9 rounded-lg font-medium hover:bg-blue-300 hover:text-black"
                >
                  Evening
                </button>
              </div>
              <div className="flex flex-col items-stretch gap-3 md:items-end">
                <div className="flex items-center gap-2 justify-start">
                  <select
                    value={updatePeriodChoice}
                    onChange={(e) => setUpdatePeriodChoice(e.target.value)}
                    className="h-9 rounded-lg text-sm border border-gray-300 px-3"
                  >
                    <option value="morning">Morning</option>
                    <option value="afternoon">Afternoon</option>
                    <option value="evening">Evening</option>
                  </select>
                  <button
                    type="button"
                    onClick={updateChosenPeriod}
                    className="h-9 px-4 text-sm rounded-lg font-semibold bg-[#0f2250] text-white hover:opacity-90 flex items-center gap-2"
                    title="Replace only the selected period in the working list"
                  >
                    <RefreshCw size={16} />
                    Update Period
                  </button>
                </div>

                <label className="inline-flex items-center gap-2 text-sm text-gray-700 justify-end">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={saveMode === "merge"}
                    onChange={(e) => setSaveMode(e.target.checked ? "merge" : "replace")}
                  />
                  Merge with existing on save (recommended)
                </label>
              </div>
            </div>

            {/* Existing vs Working */}
            <div className="mt-8 grid lg:grid-cols-2 gap-6">
              <div className="rounded-2xl bg-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-800">
                    Existing Slots (server)
                  </h3>
                  <button
                    type="button"
                    onClick={loadExistingToWorking}
                    className="text-sm px-3 py-1 rounded-lg border border-gray-300 hover:bg-white"
                    title="Copy existing slots into working list to modify"
                  >
                    Load into Working
                  </button>
                </div>

                {existingSlots.length === 0 ? (
                  <p className="text-gray-500 text-sm">No slots on server for this court.</p>
                ) : (
                  <ul className="space-y-2">
                    {existingSlots
  .slice()
  .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime))
  .map((s, idx) => (
    <li
      key={`ex-${s.startTime}-${s.endTime}-${idx}`}
      className="bg-white rounded-xl text-sm  shadow-sm px-4 py-2 flex items-center justify-between"
    >
      <div>
        <p className="text-sm text-gray-600">
          {selectedCourtName} | Full Court
        </p>
        <p className="font-semibold">
          {formatAMPM(s.startTime)} — {formatAMPM(s.endTime)}
        </p>
        {s.startDate && (
          <p className="text-xs text-gray-500">
            From {s.startDate}
          </p>
        )}
      </div>
      {/* ❗ DELETE BUTTON FOR EXISTING SLOT */}
      <button
        onClick={() => {
          setExistingSlots(prev =>
            prev.filter((_, i) => i !== idx)
          );
        }}
        className="p-2 rounded-full hover:text-red-500"
      >
        <X className="text-gray-500 hover:text-red-500 text-sm" size={16} />
      </button>
    </li>
  ))}

                  </ul>
                )}
              </div>

              <div className="rounded-2xl bg-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-800">
                    Working Slots (not saved yet)
                  </h3>
                {slotList.length > 1 && (
                    <button
                      onClick={clearAll}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {slotList.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    No working slots yet. Add or load existing.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {slotList.map((s, idx) => (
                      <li
                        key={`${s.startTime}-${s.endTime}-${idx}`}
                        className="bg-white text-sm rounded-2xl shadow-sm px-4 py-3 flex items-center justify-between"
                      >
                        <div>
                          <p className="text-sm text-gray-600">
                            {selectedCourtName} | Full Court
                          </p>
                          <p className="text-sm font-semibold tracking-wide">
                            {formatAMPM(s.startTime)} — {formatAMPM(s.endTime)}
                          </p>
                          {s.startDate && (
                            <p className="text-xs text-gray-500">
                              From {s.startDate}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-base font-medium">
                            ₹{priceToNumber(s.price)}
                          </span>
                          <button
                            onClick={() => removeSlot(idx)}
                            className="p-2 rounded-full hover:bg-gray-200"
                            aria-label="Remove slot"
                          >
                            <X className="text-gray-500 hover:text-red-500 text-sm" size={16} />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {slotList.length > 0 && (
                  <div className="mt-3 text-sm text-gray-600">
                    Total: <span className="font-semibold">₹{totalAmount}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* <div className="sticky bottom-0 left-0 right-0 border-t bg-white z-20">
          <div className="max-w-5xl mx-auto px-4 py-3 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <button
              onClick={() => navigate(-1)}
              className="h-9 px-6 rounded-xl border border-[#0f2250] text-[#0f2250] font-semibold bg-white w-full sm:w-auto"
            >
              CANCEL
            </button>

            <button
              onClick={saveAll}
              disabled={!slotList.length || !venueId || !courtId || saving}
              className={`h-9 px-6 rounded-xl font-semibold w-full sm:w-auto ${
                slotList.length && venueId && courtId && !saving
                  ? "bg-[#0f2250] text-white"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
              title="Slots (with their Start Date) will be saved to this court."
            >
              {saving
                ? "Saving…"
                : slotList.length
                ? `${slotList.length} SLOTS (₹${totalAmount})`
                : "0 SLOTS (₹0)"}
            </button>
          </div>
        </div> */}


         {/* Bottom Fixed Bar */}
          <div
            className="
              fixed bottom-0 left-0 right-0
              bg-white border-t border-gray-300 px-4 py-3 
              lg:left-64
            "
          >
            <div className="max-w-5xl mx-auto flex gap-3">
            

              
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex-[1] h-9 rounded-xl border border-[#0f2250] text-[#0f2250] font-semibold"
              >
                CANCEL
              </button>

            <button
              onClick={saveAll}
              disabled={!slotList.length || !venueId || !courtId || saving}
              className={`flex-[2] h-9 rounded-xl font-semibold ${
                slotList.length && venueId && courtId && !saving
                  ? "bg-[#0f2250] text-white"
                  : "bg-gray-200 text-gray-500 cursor-not-allowed"
              }`}
              title="Slots (with their Start Date) will be saved to this court."
            >
              {saving
                ? "Saving…"
                : slotList.length
                ? `${slotList.length} SLOTS (₹${totalAmount})`
                : "0 SLOTS (₹0)"}
            </button>
            </div>
          </div>
      </main>
    </div>
  );
}
